/** */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { setPageTitle } from 'store/actions/header';
import { GridColumn, GridRow } from 'app/member/layouts/grid';
import { Spacer } from 'app/layouts/generic';
import { SectionCard } from 'app/shared/section-card';
import { InsightList } from 'app/shared/insight-list';
import { useTenantService } from 'hooks/api/iam/tenant';
import { useWalletService } from 'hooks/api/sales/wallet';
import { useAnalystService } from 'hooks/api/agents/analyst';
import { useNumbers } from 'hooks/ui/general/numbers';
import { formatDateForDisplay } from 'app/shared/utils/date';
import { fmt } from 'utilities/format';
import { DashboardStatBar } from 'app/member/modules/dashboard/components/statbar';
import styles from './dashboard.module.css';

const RISK_LABELS = { healthy: 'Healthy', at_risk: 'At Risk', critical: 'Critical' };
const WALLET_CHANNELS = ['email', 'sms', 'push'];

const scoreColor = (score) => {
  if (score === null || score === undefined) return 'var(--neutral-dark-4)';
  if (score >= 80) return 'var(--green-primary)';
  if (score >= 60) return 'var(--orange-primary)';
  return 'var(--red-primary)';
};

const HealthBar = ({ score }) => {
  const pct = Math.min(100, Math.max(0, score ?? 0));
  const color = scoreColor(score);
  return (
    <div className={styles.healthBar}>
      <div className={styles.healthBarTop}>
        <span className={styles.healthBarScore} style={{ color }}>
          {score ?? '—'}
        </span>
        <span className={styles.healthBarDenom}>/100</span>
      </div>
      <div className={styles.healthBarTrack}>
        <div className={styles.healthBarFill} style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className={styles.healthBarLabel}>health score</span>
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div className={styles.detailRow}>
    <span className={styles.detailLabel}>{label}</span>
    <span className={styles.detailValue}>{value || '—'}</span>
  </div>
);

const TenantDashboard = () => {
  const { id: tenant_id } = useParams();
  const dispatch = useDispatch();
  const { toReadableNumber } = useNumbers();
  const { fetchTenant } = useTenantService();
  const { fetchWallets } = useWalletService();
  const { fetchTenantReport } = useAnalystService();

  const [tenant, setTenant] = useState({});
  const [wallet, setWallet] = useState({});
  const [report, setReport] = useState(null);

  useEffect(() => {
    Promise.all([
      fetchTenant(tenant_id),
      fetchWallets({ query_string: `tenant_id=${tenant_id}` }),
      fetchTenantReport(tenant_id)
    ]).then(([{ tenant: t }, { wallets }, { report: r }]) => {
      if (t) {
        setTenant(t);
        dispatch(
          setPageTitle([
            { title: 'Tenants', path: '.' },
            { title: t.name, path: `/tenants/${tenant_id}` }
          ])
        );
      }
      if (wallets?.[0]) setWallet(wallets[0]);
      if (r) setReport(r);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant_id]);

  const risk_level = report?.risk_level || null;
  const address = tenant.address || {};
  const address_parts = [address.street, address.state, address.country].filter(Boolean);

  const last_active = report?.last_active_at
    ? new Date(report.last_active_at).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    : '—';

  const report_updated = report?.last_updated
    ? new Date(report.last_updated).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      })
    : null;

  return (
    <>
      <div className={styles.reportHeader}>
        <HealthBar score={report?.health_score} />

        {risk_level && (
          <span className={`${styles.riskBadge} ${styles[risk_level]}`}>
            {RISK_LABELS[risk_level] || risk_level}
          </span>
        )}

        <div className={styles.divider} />

        <div className={styles.metaItems}>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>MRR</span>
            <span className={styles.metaValue}>{fmt(report?.mrr, 'currency')}</span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Plan</span>
            <span className={styles.metaValue}>{report?.plan || '—'}</span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Last Active</span>
            <span className={styles.metaValue}>{last_active}</span>
          </div>
          {report_updated && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Report Updated</span>
              <span className={styles.metaValue}>{report_updated}</span>
            </div>
          )}
        </div>
      </div>

      <Spacer multiple={4} />

      <GridRow num_of_columns={5} auto_flow={false}>
        <GridColumn span={3} auto_columns={false}>
          <DashboardStatBar tenant_id={tenant_id} />
        </GridColumn>
        <GridColumn span={2}>
          <SectionCard title="Account">
            <div className={styles.detailList}>
              <DetailRow label="Name" value={tenant.name} />
              <DetailRow label="Email" value={tenant.emails?.[0]} />
              <DetailRow label="Phone" value={tenant.phone_number} />
              <DetailRow label="Industry" value={tenant.industry} />
              {address_parts.length > 0 && (
                <DetailRow label="Address" value={address_parts.join(', ')} />
              )}
            </div>
            <div className={styles.detailDivider} />
            <div className={styles.detailList}>
              <DetailRow label="Status" value={report?.is_active ? 'Active' : 'Inactive'} />
              <DetailRow label="Plan" value={report?.plan} />
              <DetailRow label="MRR" value={fmt(report?.mrr, 'currency')} />
              <DetailRow label="Subscribers" value={toReadableNumber(tenant.subscriber_count)} />
            </div>
            <div className={styles.detailDivider} />
            <div className={styles.detailList}>
              <DetailRow label="Joined" value={formatDateForDisplay(tenant.created_on)} />
              <DetailRow label="Last login" value={formatDateForDisplay(tenant.last_login)} />
              <DetailRow label="Last active" value={last_active} />
            </div>
          </SectionCard>

          <Spacer multiple={4} />

          <SectionCard title="Contact Person">
            <div className={styles.detailList}>
              <DetailRow
                label="Name"
                value={[tenant.contact?.firstname, tenant.contact?.lastname]
                  .filter(Boolean)
                  .join(' ')}
              />
              <DetailRow label="Email" value={tenant.contact?.email} />
              <DetailRow label="Phone" value={tenant.contact?.phone_number} />
            </div>
          </SectionCard>

          <Spacer multiple={4} />

          <SectionCard title="Wallet">
            <table className={styles.walletTable}>
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Balance</th>
                  <th>Rate</th>
                </tr>
              </thead>
              <tbody>
                {WALLET_CHANNELS.map((ch) => (
                  <tr key={ch}>
                    <td className={styles.walletChannel}>{ch}</td>
                    <td>{toReadableNumber(wallet[ch]?.balance) || 0}</td>
                    <td>{wallet[ch]?.rate ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>

          {report?.signals?.length > 0 && (
            <>
              <Spacer multiple={4} />
              <SectionCard title="Risk Signals">
                <InsightList insights={report.signals} />
              </SectionCard>
            </>
          )}
        </GridColumn>
      </GridRow>
    </>
  );
};

export default TenantDashboard;
